import React from "react";
import axios from "axios";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "./ckEditor"; // reuse your stable CKEditor class

export const base_url = process.env.REACT_APP_BASE_URL;
export const api_type = process.env.REACT_APP_API_TYPE;

// --- CHANGE THIS to your file upload API path ---
const THUMBNAIL_UPLOAD_ENDPOINT = `${base_url}/${api_type}/cms/partner/uploads`;

// Try to read the uploaded file URL from different common shapes.
function extractUploadUrl(resp) {
  return (
    resp?.data?.url ||
    resp?.data?.location ||
    resp?.data?.file?.url ||
    resp?.url ||
    resp?.location ||
    null
  );
}

export default function AboutManage() {
  // ---- auth header ----
  const authHeader = React.useMemo(
    () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` }),
    []
  );

  // ---- list state ----
  const [rows, setRows] = React.useState([]);    // [{ id, title, description, thumbnail }]
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  // ---- add/edit modal ----
  const [open, setOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [modalError, setModalError] = React.useState("");
  const [editingId, setEditingId] = React.useState(null); // null => Add

  // form fields
  const [title, setTitle] = React.useState("");
  const [thumbnail, setThumbnail] = React.useState("");        // final URL used by Save
  const [description, setDescription] = React.useState("");

  // upload states
  const [uploading, setUploading] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState(0);
  const [uploadError, setUploadError] = React.useState("");
  const [file, setFile] = React.useState();

  // ---- delete confirm ----
  const [confirm, setConfirm] = React.useState({
    open: false, id: null, label: "", loading: false, error: ""
  });

  // ---- helpers ----
  const normalizeList = (raw) => {
    // support {data:{list:[]}} or {list:[]} or [] directly
    const list = raw?.data?.list ?? raw?.list ?? raw ?? [];
    return Array.isArray(list) ? list : [];
  };

  const stripHtml = (htmlString) => {
    if (!htmlString) return "";
    const div = document.createElement("div");
    div.innerHTML = htmlString;
    return (div.textContent || div.innerText || "").trim();
  };

  // ---- API calls (adjust endpoints if needed) ----
  const load = React.useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await axios.get(
        `${base_url}/${api_type}/cms/partner/aboutSection`,
        { headers: authHeader }
      );
      setRows(normalizeList(data));
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Failed to load About items");
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  React.useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditingId(null);
    setTitle("");
    setThumbnail("");
    setDescription("");
    setModalError("");
    setUploadError("");
    setUploading(false);
    setUploadProgress(0);
    setOpen(true);
  };

  const openEdit = async (row) => {
    setEditingId(row.id);
    // Optimistic prefill
    setTitle(row.title || "");
    setThumbnail(row.thumbnail || "");
    setDescription(row.description || "");
    setModalError("");
    setUploadError("");
    setUploading(false);
    setUploadProgress(0);
    setOpen(true);

    try {
      const { data } = await axios.get(
        `${base_url}/${api_type}/cms/partner/about/${row.id}`,
        { headers: authHeader }
      );
      const item = data?.data || data || {};
      setTitle(item.title || "");
      setThumbnail(item.thumbnail || "");
      setDescription(item.description || "");
    } catch {
      // keep optimistic values on failure
    }
  };

  const closeModal = () => {
    if (saving) return;
    setOpen(false);
    setEditingId(null);
    setTitle("");
    setThumbnail("");
    setDescription("");
    setModalError("");
    setUploadError("");
    setUploading(false);
    setUploadProgress(0);
  };

  const onSave = async () => {
  if (!title.trim()) return setModalError("Please enter a title.");
  if (!description.trim()) return setModalError("Please add some description.");

  setSaving(true);
  setModalError("");
  const formData = new FormData();

  // Append form data
  formData.append("title", title.trim());
  formData.append("description", description); // HTML from CKEditor

  // Handle file upload only when saving (thumbnail)
  if (file) {
    formData.append("file", file); // Append the actual file for upload
  } else if (thumbnail && thumbnail.includes("http")) {
    // If the thumbnail is a URL (user pasted the URL)
    formData.append("thumbnail", thumbnail);
  }

  try {
    const endpoint = editingId
      ? `${base_url}/${api_type}/cms/partner/aboutSection/${editingId}`
      : `${base_url}/${api_type}/cms/partner/aboutSection`;

    const response = editingId
      ? await axios.put(endpoint, formData, { headers: authHeader })
      : await axios.post(endpoint, formData, { headers: authHeader });
      setOpen(false);
      await load();
  } catch (e) {
    setModalError(e?.response?.data?.message || e?.message || "Save failed");
  } finally {
    setSaving(false);
  }
};



  // Delete flow with confirmation modal
  const askDelete = (row) => {
    setConfirm({ open: true, id: row.id, label: row.title, loading: false, error: "" });
  };

  const closeConfirm = () => {
    if (confirm.loading) return;
    setConfirm({ open: false, id: null, label: "", loading: false, error: "" });
  };

  const doDelete = async () => {
    if (!confirm.id) return;
    setConfirm((c) => ({ ...c, loading: true, error: "" }));
    try {
      await axios.delete(
        `${base_url}/${api_type}/cms/partner/aboutSection/${confirm.id}`,
        { headers: authHeader }
      );
      closeConfirm();
      await load();
    } catch (e) {
      setConfirm((c) => ({
        ...c,
        loading: false,
        error: e?.response?.data?.message || e?.message || "Delete failed",
      }));
    }
  };

 const onPickFile = (e) => {
  const file = e.target.files?.[0];
  console.log("File picked:", file);

  if (file) {
    // Generate a URL for the selected file (for preview)
    const fileUrl = URL.createObjectURL(file);

    // Save the file and its URL for preview
    setThumbnail(fileUrl); // Save the URL for preview in state
    setFile(file); // Save the actual file for upload
  }
};



console.log("thumbnail",thumbnail)


  const removeThumbnail = () => {
  setThumbnail(""); // Reset preview URL
  setFile(null); // Reset the actual file for upload
};


  return (
    <div>
      {/* Header */}
      <div className="bg-white p-4 rounded-lg mb-4 shadow-md">
        <div className="flex items-center justify-between">
          <h1 className="text-[#252C32] text-[24px] font-bold">About Section</h1>
          <button
            onClick={openAdd}
            className="bg-[#ffb819] px-4 py-2 rounded-lg font-montserrat"
          >
            Add About Block
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="overflow-x-auto w-full">
        {error && (
          <div className="mx-5 my-2 px-3 py-2 rounded bg-red-50 text-red-700 border border-red-200">
            {error}
          </div>
        )}

        {loading ? (
          <div className="p-5 text-sm text-gray-600">Loading…</div>
        ) : (
          <div className="overflow-hidden border rounded-2xl shadow-sm bg-white">
            <div className="px-4 py-3 border-b bg-gray-50/60">
              <h2 className="text-base font-semibold text-gray-900">About Blocks</h2>
            </div>

            <table className="min-w-full table-fixed">
              <colgroup>
                <col style={{ width: "15%" }} /> {/* thumbnail */}
                <col style={{ width: "25%" }} /> {/* title */}
                <col style={{ width: "45%" }} /> {/* description preview */}
                <col style={{ width: "15%" }} /> {/* actions */}
              </colgroup>
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="text-left p-3 border-b">Thumbnail</th>
                  <th className="text-left p-3 border-b">Title</th>
                  <th className="text-left p-3 border-b">Description</th>
                  <th className="text-center p-3 border-b">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length ? rows.map((r) => {
                  const preview = stripHtml(r.description);
                  return (
                    <tr key={r.id} className="odd:bg-white even:bg-gray-50">
                      <td className="p-3 border-b align-top">
                        {r.thumbnail ? (
                          <img
                            src={r.thumbnail}
                            alt={r.title || "thumbnail"}
                            className="h-14 w-20 object-cover rounded border"
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="p-3 border-b align-top">
                        <div className="font-medium text-gray-900">{r.title || "—"}</div>
                      </td>
                      <td className="p-3 border-b align-top">
                        <div
                          className="text-gray-700"
                          style={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                          title={preview}
                        >
                          {preview || "—"}
                        </div>
                      </td>
                      <td className="p-3 border-b align-top">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            className="px-3 py-1.5 rounded border hover:bg-gray-50"
                            onClick={() => openEdit(r)}
                          >
                            Edit
                          </button>
                          <button
                            className="px-3 py-1.5 rounded border text-red-600 hover:bg-red-50"
                            onClick={() => askDelete(r)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr>
                    <td className="p-4 text-sm text-gray-500" colSpan={4}>
                      No about blocks found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ---------- Add/Edit Modal ---------- */}
      {open && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={closeModal} />
          <div className="relative bg-white w-[96%] max-w-4xl rounded-xl shadow-2xl overflow-hidden z-[1001]">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">
                {editingId ? "Edit About Block" : "Add About Block"}
              </h2>
            </div>

            <div className="px-6 py-5 space-y-4">
              {modalError && (
                <div className="px-3 py-2 rounded bg-red-50 text-red-700 border border-red-200">
                  {modalError}
                </div>
              )}

              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-1">Title</span>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter title"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
                  disabled={saving}
                />
              </label>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Description with CKEditor */}
                <label className="md:col-span-2 flex flex-col">
                  <span className="text-sm font-medium text-gray-700 mb-2">Description</span>
                  <div className="border rounded-md overflow-hidden">
                    <CKEditor
                      editor={Editor}
                      onReady={(ed) => {
                        console.log("CKEditor ready with data:", description);
                        ed.setData(description || "");
                      }}
                      onChange={(_, ed) => {
                        console.log("CKEditor data changed:", ed.getData());
                        setDescription(ed.getData());
                      }}
                    />
                  </div>
                </label>

                {/* Thumbnail upload + URL fallback */}
            
                  {/* Thumbnail upload + URL fallback */}
<div className="flex flex-col">
  <span className="text-sm font-medium text-gray-700 mb-1">Thumbnail</span>

  {/* file picker */}
  <label className="flex items-center justify-center h-10 px-3 rounded-lg border cursor-pointer hover:bg-gray-50">
    <input
      type="file"
      accept="image/*"
      className="hidden"
      onChange={onPickFile}
      disabled={saving || uploading}
    />
    <span className="text-sm">{uploading ? "Uploading…" : "Choose file"}</span>
  </label>

  {/* progress / error */}
  {uploading && (
    <div className="mt-2">
      <div className="w-full h-2 bg-gray-200 rounded">
        <div
          className="h-2 bg-blue-500 rounded"
          style={{ width: `${uploadProgress}%` }}
        />
      </div>
      <div className="text-xs text-gray-600 mt-1">{uploadProgress}%</div>
    </div>
  )}
  {uploadError && (
    <div className="mt-2 px-2 py-1 rounded text-sm bg-red-50 text-red-700 border border-red-200">
      {uploadError}
    </div>
  )}

  {/* direct URL input (optional) */}
  <input
    value={thumbnail || ""}
    onChange={(e) => setThumbnail(e.target.value)}
    placeholder="Or paste image URL (https://…)"
    className="mt-3 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
    disabled={saving || uploading}
  />

  {/* Preview + remove */}
  <div className="mt-3">
    {thumbnail ? (
      <div className="relative">
        <img
          src={thumbnail}
          alt="Preview"
          className="h-28 w-full object-cover rounded border"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
        <button
          type="button"
          onClick={removeThumbnail}
          className="absolute top-2 right-2 px-2 py-1 text-xs rounded bg-white/90 border hover:bg-white"
          disabled={saving || uploading}
        >
          Remove
        </button>
      </div>
    ) : (
      <div className="h-28 w-full rounded border flex items-center justify-center text-gray-400 text-sm">
        No preview
      </div>
    )}
  </div>
</div>

              </div>
            </div>

            <div className="px-6 py-4 border-t flex items-center justify-between">
              <button
                onClick={closeModal}
                className="px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-50"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={onSave}
                disabled={saving || uploading || !title.trim() || !description.trim()}
                className="px-5 py-2 rounded-lg bg-[#1a73e8] text-white hover:opacity-95 disabled:opacity-60"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Delete Confirmation Modal ---------- */}
      {confirm.open && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={closeConfirm} />
          <div className="relative bg-white w-[96%] max-w-md rounded-xl shadow-2xl overflow-hidden z-[1101]">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Confirm Delete</h2>
            </div>
            <div className="px-6 py-5 space-y-3">
              {confirm.error && (
                <div className="px-3 py-2 rounded bg-red-50 text-red-700 border border-red-200">
                  {confirm.error}
                </div>
              )}
              <p className="text-sm text-gray-700">
                Are you sure you want to delete <strong>{confirm.label || "this item"}</strong>?
              </p>
              <p className="text-xs text-gray-500">This action cannot be undone.</p>
            </div>
            <div className="px-6 py-4 border-t flex items-center justify-between">
              <button
                onClick={closeConfirm}
                className="px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-50"
                disabled={confirm.loading}
              >
                Cancel
              </button>
              <button
                onClick={doDelete}
                disabled={confirm.loading}
                className="px-5 py-2 rounded-lg bg-red-600 text-white hover:opacity-95 disabled:opacity-60"
              >
                {confirm.loading ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Editor height tweak */}
      <style>{`
        .ck-editor__editable[role="textbox"] { min-height: 200px; }
      `}</style>
    </div>
  );
}
